import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";

export default function SecurityPolicyPage() {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="font-sans text-slate-800 bg-white">
      <Navbar />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center text-[#1AB790] hover:text-[#17a380] transition-colors mb-4"
          >
            ← Back to Home
          </Link>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 mb-4">
            Revisr Security Policy
          </h1>
          <div className="text-sm text-gray-600 space-y-1">
            <p><strong>Effective Date:</strong> 1/11/2025</p>
            <p><strong>Last Updated:</strong> 13/1/2026</p>
          </div>
        </div>

        <div className="prose prose-lg max-w-none space-y-8">
          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-4">1. Overview</h2>
            <p className="text-gray-700 leading-relaxed">
              Revisr Pty Ltd (ACN 693 866 447) trading as Revisr ("Revisr", "we", "our") is committed to safeguarding the confidentiality, integrity, and availability of all information entrusted to us.
            </p>
            <p className="text-gray-700 leading-relaxed">
              This Security Policy explains how we protect customer data and systems used to deliver the Revisr.ai platform.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Our controls follow recognised frameworks including the Australian Cyber Security Centre's Essential Eight, ISO 27001, and the Privacy and Data Protection Act 2014 (Vic).
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-4">2. Scope</h2>
            <p className="text-gray-700 leading-relaxed">
              This policy applies to all Revisr systems, infrastructure, and personnel that store or process:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Customer documents and metadata uploaded to the Revisr platform</li>
              <li>Personal information of users (as described in our Privacy Policy)</li>
              <li>Internal operational data supporting the service</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-4">3. Data Security and Encryption</h2>
            <div className="space-y-3">
              <p className="text-gray-700 leading-relaxed">
                <strong>Data at Rest:</strong> All customer data is encrypted using AES-256 or stronger algorithms within our cloud storage environment.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>Data in Transit:</strong> All communications between browsers, APIs, and our servers are protected with TLS 1.3.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>Backups:</strong> Automated, encrypted backups are retained for disaster recovery and securely purged after 90 days.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>Secrets Management:</strong> API keys and credentials are stored in managed secret vaults with audit logging.
              </p>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-4">4. Hosting and Infrastructure</h2>
            <p className="text-gray-700 leading-relaxed">
              Revisr is hosted primarily on Australian-based, ISO 27001-certified cloud infrastructure.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Where optional integrations require international processing, equivalent or stronger controls apply.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Infrastructure access is restricted via VPN and multi-factor authentication (MFA).
            </p>
            <p className="text-gray-700 leading-relaxed">
              Continuous monitoring and intrusion-detection tools track system health and anomalies.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-4">5. Access Control</h2>
            <div className="space-y-3">
              <p className="text-gray-700 leading-relaxed">
                <strong>Role-Based Access Control (RBAC):</strong> Users are granted only the minimum permissions required for their role.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>Multi-Factor Authentication:</strong> MFA is mandatory for all internal administrators.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>Session Management:</strong> Automatic time-outs and re-authentication protect idle sessions.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>Employee Vetting:</strong> All staff and contractors sign confidentiality and acceptable-use agreements before being granted access.
              </p>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-4">6. Application Security</h2>
            <p className="text-gray-700 leading-relaxed">
              Secure-development practices include peer review, code scanning, and dependency vulnerability testing.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Regular penetration tests and third-party audits assess application and API resilience.
            </p>
            <p className="text-gray-700 leading-relaxed">
              External libraries and LLM connectors are sandboxed and permission-scoped.
            </p>
            <p className="text-gray-700 leading-relaxed">
              AI and LLM models never train on or retain customer data beyond the active session.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-4">7. Incident Response</h2>
            <p className="text-gray-700 leading-relaxed">
              Revisr maintains an Incident Response Plan aligned with ACSC and OAIC guidance:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li><strong>Detection & Containment:</strong> Identify and isolate the affected system.</li>
              <li><strong>Assessment:</strong> Evaluate scope and potential impact.</li>
              <li><strong>Notification:</strong> Notify affected customers and the Office of the Australian Information Commissioner (OAIC) if the incident qualifies under the Notifiable Data Breaches scheme.</li>
              <li><strong>Remediation:</strong> Apply corrective measures and document lessons learned.</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-4">8. Business Continuity and Disaster Recovery</h2>
            <p className="text-gray-700 leading-relaxed">
              Redundant systems ensure high availability of the platform.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Daily encrypted backups and tested recovery procedures enable restoration within established recovery-time objectives.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Critical dependencies are monitored for uptime and failover readiness.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-4">9. Third-Party Service Providers</h2>
            <p className="text-gray-700 leading-relaxed">
              We engage reputable third-party vendors (e.g. cloud hosting, payment processors) under strict data-processing agreements.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Each provider must demonstrate compliance with:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Australian Privacy Principles</li>
              <li>ISO 27001 or equivalent certification</li>
              <li>Contractual confidentiality and breach-notification requirements</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-4">10. Customer Responsibilities</h2>
            <p className="text-gray-700 leading-relaxed">
              To maintain shared security, customers must:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Protect their account credentials and enable MFA where available.</li>
              <li>Validate all AI-generated outputs before use in official or regulated documents.</li>
              <li>Configure permissions within their workspace responsibly and revoke unused access.</li>
              <li>Notify Revisr immediately of any suspected compromise of their account.</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-4">11. Ongoing Improvement</h2>
            <p className="text-gray-700 leading-relaxed">
              Revisr conducts regular reviews of its security posture and updates controls to meet evolving threats and industry standards.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Security awareness training is mandatory for all team members and refreshed annually.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-4">12. Contact</h2>
            <p className="text-gray-700 leading-relaxed">
              Questions or security-related concerns can be sent to:
            </p>
            <p className="text-gray-700 leading-relaxed">
              📧 <a href="mailto:support@revisr.ai" className="text-[#1AB790] hover:text-[#17a380] underline">support@revisr.ai</a>
            </p>
            <p className="text-gray-700 leading-relaxed">
              ✉️ Revisr Pty Ltd (ACN 693 866 447) trading as Revisr<br />
              Melbourne, Victoria, Australia
            </p>
            <p className="text-gray-700 leading-relaxed">
              For urgent incidents, include "Security Incident" in the subject line.
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}



